import { AlertTriangle } from 'lucide-react';

/**
 * Details shown in the "Discount exceeds the receipt base" warning. Keeping
 * them in one object makes the modal easy to reuse from both the create and
 * edit receipt screens.
 */
export interface ReceiptDiscountLimitDetails {
  /** Total discount % on the receipt (item discounts + extra discount). */
  discountPercent: number;
  /** Maximum allowed discount %, configured in Settings → General. */
  basePercent: number;
}

/**
 * Warning shown when the receipt's total discount % is higher than the Receipt
 * Base Discount configured in Settings → General. The base is the hospital's
 * maximum discount budget, so submission is blocked until the discount is
 * reduced back within it.
 */
export function ReceiptDiscountLimitModal({
  open,
  details,
  onClose,
}: {
  open: boolean;
  details: ReceiptDiscountLimitDetails;
  onClose: () => void;
}) {
  if (!open) return null;

  const { discountPercent, basePercent } = details;
  const excessPercent = Math.max(0, Number((discountPercent - basePercent).toFixed(2)));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-md space-y-4 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-warning/10 flex items-center justify-center shrink-0">
            <AlertTriangle size={22} className="text-warning" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-text">Discount exceeds the receipt base</h3>
            <p className="text-sm text-text-muted mt-1">
              The total discount % cannot be higher than the Receipt Base Discount configured in
              Settings → General. Reduce the discount before saving this receipt.
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-background p-3 space-y-2 text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="text-text-muted">Receipt Base Discount</span>
            <span className="font-medium text-text">{basePercent}%</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-text-muted">Discount %</span>
            <span className="font-medium text-danger">{discountPercent}%</span>
          </div>
          <div className="h-px bg-border" />
          <div className="flex items-center justify-between gap-3">
            <span className="text-text-muted">Excess</span>
            <span className="font-bold text-danger">{excessPercent}%</span>
          </div>
        </div>

        <div className="flex items-center justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
          >
            Go Back &amp; Fix
          </button>
        </div>
      </div>
    </div>
  );
}
